import { Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useMutation } from "react-query";
import { toast } from "react-toastify";
import { FormattedMessage, useIntl } from "react-intl";
import {
  ProductService,
  downloadImportTemplate,
} from "../../../services/product";
import { ProductImportJob } from "../../../types/product-import-job";
import Dropzone from "../../../components/ContentManager/Dropzone";
import { downloadFile } from "../../../utils/download";
import Dropdown, {
  DropdownMenu,
  DropdownMenuOption,
} from "../../../components/Buttons/Dropdown";

interface IStep1Props {
  setStep: (step: number) => void;
  setJob: (job: ProductImportJob) => void;
}

function Step1({ setStep, setJob }: IStep1Props) {
  const intl = useIntl();
  const { handleSubmit, setValue, watch, register } = useForm<{
    file: File;
    updateIfExists: boolean;
    publishProducts: boolean;
    categoryStrategy: "create" | "skip";
  }>({
    defaultValues: {
      publishProducts: true,
      categoryStrategy: "create",
    },
  });
  const file = watch("file");

  const handleDownloadTemplate = async () => {
    try {
      const response = await downloadImportTemplate();
      downloadFile(response.data, "plantilla-productos.csv");
      toast.success(
        intl.formatMessage({ id: "product.import.template-downloaded" }),
      );
    } catch (error) {
      toast.error(
        intl.formatMessage({ id: "product.import.template-download-error" }),
      );
    }
  };

  const handleRemoveFile = () => {
    setValue("file", undefined as any);
  };

  const handleChangeFile = () => {
    // Create a file input element and trigger click
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".csv";
    input.onchange = (e) => {
      const selectedFile = (e.target as HTMLInputElement).files?.[0];
      if (selectedFile) {
        setValue("file", selectedFile);
      }
    };
    input.click();
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const fileOptions: DropdownMenuOption<any>[] = [
    {
      text: intl.formatMessage({ id: "Change" }),
      icon: "ni ni-edit",
      click: () => handleChangeFile(),
    },
    {
      text: intl.formatMessage({ id: "Remove" }),
      icon: "ni ni-trash",
      click: () => handleRemoveFile(),
    },
  ];

  const importProductsMutation = useMutation({
    mutationFn: async (data: {
      file: File;
      updateIfExists: boolean;
      publishProducts: boolean;
      categoryStrategy: "create" | "skip";
    }) => {
      if (data.file) {
        return await ProductService.import(
          data.file,
          data.updateIfExists,
          data.publishProducts,
          data.categoryStrategy,
        );
      }
    },
    onSuccess(data) {
      if (!data) return;
      setJob(data);
      setStep(2);
    },
    onError() {
      toast.error(
        intl.formatMessage({
          id: "There was an error importing the products.",
        }),
      );
    },
  });

  const onSubmit = handleSubmit(async (data) => {
    await importProductsMutation.mutateAsync(data);
  });

  return (
    <div className="card card-bordered card-preview">
      <div className="card-inner">
        <div className="preview-block">
          <div className="mb-4">
            <div className="alert alert-info">
              <div className="d-flex justify-content-between align-items-start">
                <div className="flex-grow-1">
                  <h6 className="alert-heading">
                    <FormattedMessage id="product.import.template-info.title" />
                  </h6>
                  <p className="mb-2">
                    <FormattedMessage id="product.import.template-info.description" />
                  </p>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleDownloadTemplate}
                    className="me-2"
                  >
                    <em className="icon ni ni-download me-1"></em>
                    <FormattedMessage id="Download template" />
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {!file ? (
            <Dropzone onFileAccepted={(file) => setValue("file", file)} />
          ) : (
            <div className="upload-zone dropzone">
              <div className="text-center py-1 position-relative">
                <div className="d-inline-block position-relative">
                  <div
                    className="nk-file-icon"
                    style={{
                      width: "80px",
                      height: "80px",
                      marginBottom: "16px",
                    }}
                  >
                    <span
                      className="nk-file-icon-type"
                      style={{ width: "100%", height: "100%" }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 72 72"
                        style={{ width: "100%", height: "100%" }}
                      >
                        <path
                          d="M49,61H23a5.0147,5.0147,0,0,1-5-5V16a5.0147,5.0147,0,0,1,5-5H40.9091L54,22.1111V56A5.0147,5.0147,0,0,1,49,61Z"
                          style={{ fill: "#e3edfc" }}
                        ></path>
                        <path
                          d="M54,22.1111H44.1818a3.3034,3.3034,0,0,1-3.2727-3.3333V11s1.8409.2083,6.9545,4.5833C52.8409,20.0972,54,22.1111,54,22.1111Z"
                          style={{ fill: "#b7d0ea" }}
                        ></path>
                        <path
                          d="M19.03,59A4.9835,4.9835,0,0,0,23,61H49a4.9835,4.9835,0,0,0,3.97-2Z"
                          style={{ fill: "#c4dbf2" }}
                        ></path>
                        <path
                          d="M42,31H30a3.0033,3.0033,0,0,0-3,3V45a3.0033,3.0033,0,0,0,3,3H42a3.0033,3.0033,0,0,0,3-3V34A3.0033,3.0033,0,0,0,42,31ZM29,38h6v3H29Zm8,0h6v3H37Zm6-4v2H37V33h5A1.001,1.001,0,0,1,43,34ZM30,33h5v3H29V34A1.001,1.001,0,0,1,30,33ZM29,45V43h6v3H30A1.001,1.001,0,0,1,29,45Zm13,1H37V43h6v2A1.001,1.001,0,0,1,42,46Z"
                          style={{ fill: "#36c684" }}
                        ></path>
                      </svg>
                    </span>
                  </div>

                  {/* Three dots menu positioned at top-right of icon */}
                  <div
                    className="position-absolute"
                    style={{
                      top: "0",
                      right: "0",
                      transform: "translate(50%, -50%)",
                    }}
                  >
                    <Dropdown
                      icon
                      trigger
                      text={<em className="icon ni ni-more-h" />}
                    >
                      <DropdownMenu options={fileOptions} entity={file} />
                    </Dropdown>
                  </div>
                </div>

                {/* File information centered below icon */}
                <div>
                  <h6 className="mb-1">{file.name}</h6>
                  <small className="text-muted">
                    {formatFileSize(file.size)} • {file.type || "CSV"}
                  </small>
                </div>
              </div>
            </div>
          )}

          <div className="mt-4">
            <hr />

            <h4 className="mb-3 mt-4">
              <FormattedMessage id="More options" />
            </h4>
            <Row>
              <Col md={6}>
                <div className="form-check form-switch mb-1">
                  <input
                    {...register("updateIfExists")}
                    type="checkbox"
                    id="update-if-exists-switch"
                    className="form-check-input"
                  />
                  <label
                    htmlFor="update-if-exists-switch"
                    className="form-check-label"
                  >
                    <FormattedMessage id="product.import.update-if-exists" />
                  </label>
                </div>
                <div className="form-check form-switch mb-1">
                  <input
                    {...register("publishProducts")}
                    type="checkbox"
                    id="publish-products-switch"
                    className="form-check-input"
                  />
                  <label
                    htmlFor="publish-products-switch"
                    className="form-check-label"
                  >
                    <FormattedMessage id="product.import.publish-products" />
                  </label>
                </div>
              </Col>
              <Col md={6}>
                <div className="form-group">
                  <label
                    className="form-label"
                    htmlFor="category-strategy-select"
                  >
                    <FormattedMessage id="product.import.category-strategy" />
                  </label>
                  <div className="form-control-wrap">
                    <select
                      {...register("categoryStrategy")}
                      className="form-select"
                      id="category-strategy-select"
                    >
                      <option value="create">
                        {intl.formatMessage({
                          id: "product.import.category-strategy.create",
                        })}
                      </option>
                      <option value="skip">
                        {intl.formatMessage({
                          id: "product.import.category-strategy.skip",
                        })}
                      </option>
                    </select>
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </div>
        <div className="mt-2 text-right">
          <hr />

          <Button
            disabled={importProductsMutation.isLoading || !file}
            onClick={onSubmit}
            size="lg"
          >
            {importProductsMutation.isLoading ? (
              <Spinner />
            ) : (
              <em className="icon ni ni-upload-cloud me-2"></em>
            )}{" "}
            <FormattedMessage id="Import" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default Step1;
